import { useState } from 'react';
import { useCompanyContext } from '@/hooks/use-company-context';
import { useGpsCommands } from '@/hooks/useGpsCommands';
import { useGpsDeviceManagement } from '@/hooks/useGpsDeviceManagement';
import { GpsLicenseGate } from '@/components/gps/GpsLicenseGate';
import { CommandPanel } from '@/components/gps/CommandPanel';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Terminal, RefreshCw, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function GpsCommandsPage() {
  const { companyId } = useCompanyContext();
  const { commands, isLoading, sendCommand, pendingCount } = useGpsCommands(companyId);
  const { devices } = useGpsDeviceManagement(companyId);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Loader2 className="h-4 w-4 animate-spin text-yellow-600" />;
      case 'sent':
        return <Clock className="h-4 w-4 text-blue-600" />;
      case 'ack':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      pending: 'secondary',
      sent: 'default',
      ack: 'default',
      failed: 'destructive',
    };

    const labels: Record<string, string> = {
      pending: 'Pendente',
      sent: 'Enviado',
      ack: 'Confirmado',
      failed: 'Falhou',
    };

    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  const handleSendCommand = (commandType: string, payload?: Record<string, any>) => {
    if (!selectedDeviceId) {
      alert('Selecione um dispositivo GPS primeiro');
      return;
    }

    sendCommand.mutate({
      device_id: selectedDeviceId,
      command_type: commandType,
      payload,
    });
  };

  return (
    <GpsLicenseGate>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Terminal className="h-8 w-8" />
              Comandos GPS
            </h1>
            <p className="text-muted-foreground mt-1">
              Envie comandos para dispositivos GPS em tempo real
            </p>
          </div>
          <Button variant="outline" size="icon">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Total Enviados</div>
            <div className="text-2xl font-bold">{commands?.length || 0}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Pendentes</div>
            <div className="text-2xl font-bold text-yellow-600">{pendingCount}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Confirmados</div>
            <div className="text-2xl font-bold text-green-600">
              {commands?.filter(c => c.status === 'ack').length || 0}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Falhas</div>
            <div className="text-2xl font-bold text-red-600">
              {commands?.filter(c => c.status === 'failed').length || 0}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Command Panel */}
          <div className="space-y-4">
            <Card className="p-4">
              <h2 className="text-lg font-semibold mb-4">Selecionar Dispositivo</h2>
              <Select value={selectedDeviceId} onValueChange={setSelectedDeviceId}>
                <SelectTrigger>
                  <SelectValue placeholder="Escolha um dispositivo GPS" />
                </SelectTrigger>
                <SelectContent>
                  {devices?.map((device) => (
                    <SelectItem key={device.id} value={device.id}>
                      {device.code} - {device.imei_or_uid}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Card>

            {selectedDeviceId && (
              <CommandPanel
                deviceId={selectedDeviceId}
                onSendCommand={handleSendCommand}
                isLoading={sendCommand.isPending}
              />
            )}
          </div>

          {/* Command History */}
          <div>
            <Card className="p-4">
              <h2 className="text-lg font-semibold mb-4">Histórico de Comandos</h2>
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {commands?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Terminal className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum comando enviado ainda</p>
                  </div>
                ) : (
                  commands?.map((command) => (
                    <Card key={command.id} className="p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(command.status)}
                          <span className="font-semibold">{command.command_type}</span>
                        </div>
                        {getStatusBadge(command.status)}
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <div className="flex justify-between">
                          <span>Criado:</span>
                          <span>
                            {formatDistanceToNow(new Date(command.created_at), {
                              addSuffix: true,
                              locale: ptBR,
                            })}
                          </span>
                        </div>
                        {command.error_message && (
                          <div className="text-red-600 text-xs mt-2">
                            {command.error_message}
                          </div>
                        )}
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </GpsLicenseGate>
  );
}
